"use client";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";

gsap.registerPlugin(CustomEase);

// Easing curves
CustomEase.create("snapNav", "M0,0 C0.9,0,0.1,1,1,1");
CustomEase.create("snapNavClose", "M0,0 C0.1,0,0.9,1,1,1");

interface MobileNavMenuProps {
  isOpen: boolean;
  className?: string;
  children: React.ReactNode;
}

const MobileNavMenu: React.FC<MobileNavMenuProps> = ({
  isOpen,
  className = "",
  children,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    if (!menuRef.current) return;

    const tl = gsap.timeline({
      paused: true,
      onReverseComplete: () => {
        if (menuRef.current) menuRef.current.style.display = "none";
      },
    });

    // Opening animation
    tl.fromTo(
      menuRef.current,
      {
        scale: 0.9,
        y: "-100%",
        borderBottomLeftRadius: 90,
        borderBottomRightRadius: 90,
        autoAlpha: 1,
      },
      {
        scale: 1,
        y: 0,
        autoAlpha: 1,
        duration: 1.2,
        ease: "snapNav",
      }
    );

    // Keep rounded corners until 60% of the animation,
    // then animate them to 0px for the last 40%.
    tl.to(
      menuRef.current,
      {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        duration: 0.4, // last 40% of total duration
        ease: "snapNavClose",
      },
      0.8 // start this step at 60% of the timeline
    );

    tlRef.current = tl;
  }, []);

  useEffect(() => {
    if (!tlRef.current || !menuRef.current) return;

    if (isOpen) {
      menuRef.current.style.display = "block";
      tlRef.current.play();
    } else {
      tlRef.current.reverse();
    }
  }, [isOpen]);

  return (
    <div
      ref={menuRef}
      className={`fixed top-0 left-0 w-screen h-screen bg-[#f5f5f5] z-50 origin-top overflow-hidden ${className}`}
    >
      <div className="flex flex-col items-center justify-center h-full text-[#0d1821] text-2xl font-medium">
        {children}
      </div>
    </div>
  );
};

export default MobileNavMenu;
